import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type FontSize = 'Tiny (12px)' | 'Small (13px)' | 'Medium (14px)' | 'Default (16px)';

interface FontContextType {
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
}

const STORAGE_KEY = 'global_font_size';
const FontContext = createContext<FontContextType | undefined>(undefined);

const sizeMap: Record<FontSize, string> = {
  'Tiny (12px)': '12px',
  'Small (13px)': '13px',
  'Medium (14px)': '14px',
  'Default (16px)': '16px',
};

function applyFontSize(size: FontSize) {
  const html = document.documentElement;
  html.style.fontSize = sizeMap[size] || '16px';
}

export function FontProvider({ children }: { children: ReactNode }) {
  const [fontSize, setFontSizeState] = useState<FontSize>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as FontSize;
    if (stored && sizeMap[stored]) {
      return stored;
    }
    return 'Default (16px)';
  });

  useEffect(() => {
    applyFontSize(fontSize);
    localStorage.setItem(STORAGE_KEY, fontSize);
  }, [fontSize]);

  const setFontSize = (newSize: FontSize) => {
    setFontSizeState(newSize);
  };

  return (
    <FontContext.Provider value={{ fontSize, setFontSize }}>
      {children}
    </FontContext.Provider>
  );
}

export function useFont() {
  const context = useContext(FontContext);
  if (context === undefined) {
    throw new Error('useFont must be used within a FontProvider');
  }
  return context;
}
